import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo, BRAND_NAME } from "../components/ui/Logo";
import { submitPublicContactForm } from "../lib/api";

const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || "contacto@aurorageriatrica.com";

const initialForm = {
  full_name: "",
  email: "",
  specialty: "",
  country: "",
  phone: "",
  message: "",
};

export default function Contact() {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.full_name.trim()) {
      setError("El nombre completo es obligatorio.");
      return;
    }
    if (!formData.email.trim()) {
      setError("El email es obligatorio.");
      return;
    }
    if (!formData.message.trim()) {
      setError("El mensaje es obligatorio.");
      return;
    }
    setLoading(true);
    try {
      await submitPublicContactForm(formData);
      setSubmittedEmail(formData.email.trim());
      setSuccess(true);
      setFormData(initialForm);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string" ? detail : "Error al enviar. Intenta de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-8 text-center shadow-sm">
            <p className="text-lg font-semibold text-emerald-900">✓ Mensaje recibido</p>
            <p className="mt-2 text-sm text-emerald-800">
              Te contactaremos pronto en {submittedEmail}
            </p>
            <Link
              to="/"
              className="mt-6 inline-block text-sm font-medium text-sky-700 hover:text-sky-800 underline"
            >
              Volver al inicio
            </Link>
          </div>
          <footer className="mt-12 text-center text-xs text-slate-500 space-y-2">
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
              <Link to="/" className="hover:text-sky-600">
                Inicio
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/precios" className="hover:text-sky-600">
                Precios
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/demo-clinico" className="hover:text-sky-600">
                Demo
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/privacidad" className="hover:text-sky-600">
                Privacidad
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/terminos" className="hover:text-sky-600">
                Términos
              </Link>
            </div>
            <p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-sky-700 hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p>© 2026 Aurora Geriátrica, S.A.</p>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <header className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <Logo variant="icon" size="md" className="h-12 w-12" alt={BRAND_NAME} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Contáctanos</h1>
          <p className="mt-2 text-slate-600 text-sm">Responderemos en menos de 24 horas</p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4"
        >
          <div>
            <label htmlFor="full_name" className="block text-xs font-medium text-slate-700 mb-1">
              Nombre completo <span className="text-red-600">*</span>
            </label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              required
              value={formData.full_name}
              onChange={handleChange}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-slate-700 mb-1">
              Email <span className="text-red-600">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label htmlFor="specialty" className="block text-xs font-medium text-slate-700 mb-1">
              Especialidad médica
            </label>
            <input
              id="specialty"
              name="specialty"
              type="text"
              value={formData.specialty}
              onChange={handleChange}
              placeholder="Geriatría, Medicina interna..."
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label htmlFor="country" className="block text-xs font-medium text-slate-700 mb-1">
              País
            </label>
            <input
              id="country"
              name="country"
              type="text"
              value={formData.country}
              onChange={handleChange}
              placeholder="Guatemala, México, Colombia..."
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-xs font-medium text-slate-700 mb-1">
              Teléfono / WhatsApp
            </label>
            <input
              id="phone"
              name="phone"
              type="text"
              value={formData.phone}
              onChange={handleChange}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-xs font-medium text-slate-700 mb-1">
              Mensaje <span className="text-red-600">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              required
              value={formData.message}
              onChange={handleChange}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y min-h-[5rem]"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-sky-700 text-white text-sm font-semibold hover:bg-sky-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Enviando..." : "Enviar mensaje"}
          </button>
        </form>

        <footer className="mt-12 text-center text-xs text-slate-500 space-y-2">
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
            <Link to="/" className="hover:text-sky-600">
              Inicio
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/precios" className="hover:text-sky-600">
              Precios
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/demo-clinico" className="hover:text-sky-600">
              Demo
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/privacidad" className="hover:text-sky-600">
              Privacidad
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/terminos" className="hover:text-sky-600">
              Términos
            </Link>
          </div>
          <p>
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-sky-700 hover:underline">
              {CONTACT_EMAIL}
            </a>
          </p>
          <p>© 2026 Aurora Geriátrica, S.A.</p>
        </footer>
      </div>
    </div>
  );
}
